import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Crown, Users } from 'lucide-react';

const GROUP_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

export default function GroupFormModal({
  open, onClose, editingGroup, groupFormData, setGroupFormData,
  selectedStudents, toggleStudent, students, submitting, onSubmit
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingGroup ? 'Editar Grupo' : 'Nuevo Grupo'}
            </DialogTitle>
            <DialogDescription>
              {editingGroup ? 'Modifica los datos del grupo y sus miembros' : 'Crea un grupo y selecciona los estudiantes que lo conformarán'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left column - Group info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Nombre del Grupo *</Label>
                  <Input
                    placeholder="Ej: Equipo Alpha"
                    value={groupFormData.name}
                    onChange={(e) => setGroupFormData({...groupFormData, name: e.target.value})}
                    required
                    data-testid="group-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descripción (opcional)</Label>
                  <Input
                    placeholder="Descripción del grupo..."
                    value={groupFormData.description}
                    onChange={(e) => setGroupFormData({...groupFormData, description: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex gap-2 flex-wrap">
                    {GROUP_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`w-8 h-8 rounded-lg border-2 transition-all ${
                          groupFormData.color === color ? 'border-slate-900 scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setGroupFormData({...groupFormData, color})}
                      />
                    ))}
                  </div>
                </div>
                {selectedStudents.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-amber-500" />
                      Líder del Grupo (opcional)
                    </Label>
                    <Select 
                      value={groupFormData.leader_user_id || 'none'} 
                      onValueChange={(value) => setGroupFormData({...groupFormData, leader_user_id: value === 'none' ? '' : value})}
                    >
                      <SelectTrigger data-testid="select-leader">
                        <SelectValue placeholder="Seleccionar líder..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin líder</SelectItem>
                        {students.filter(s => selectedStudents.includes(s.user_id)).map((student) => (
                          <SelectItem key={student.user_id} value={student.user_id}>
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                                {student.photo_url ? (
                                  <img src={student.photo_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-xs">{student.user_name?.charAt(0)}</span>
                                )}
                              </div>
                              <span>{student.user_name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      El líder puede coordinar el equipo y tiene un distintivo especial
                    </p>
                  </div>
                )}
              </div>
              
              {/* Right column - Member selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Seleccionar Miembros</Label>
                  <Badge variant="outline">{selectedStudents.length} seleccionados</Badge>
                </div>
                <div className="flex items-center gap-2 pb-2 border-b">
                  <Checkbox 
                    checked={selectedStudents.length === students.length && students.length > 0}
                    onCheckedChange={() => {
                      if (selectedStudents.length === students.length) {
                        toggleStudent('clear_all');
                      } else {
                        toggleStudent('select_all');
                      }
                    }}
                  />
                  <Label className="cursor-pointer text-sm">Seleccionar todos ({students.length})</Label>
                </div>
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {students.map(student => {
                    const isSelected = selectedStudents.includes(student.user_id);
                    return (
                      <label 
                        key={student.user_id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                      >
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={() => toggleStudent(student.user_id)}
                        />
                        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {student.photo_url ? (
                            <img src={student.photo_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs font-medium text-slate-600">
                              {student.user_name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{student.user_name}</p>
                        </div>
                      </label>
                    );
                  })}
                  {students.length === 0 && (
                    <div className="text-center py-4 text-slate-500">
                      <Users className="w-6 h-6 mx-auto mb-1 opacity-50" />
                      <p className="text-sm">No hay estudiantes</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={submitting} data-testid="submit-group">
              {submitting ? 'Guardando...' : (editingGroup ? 'Guardar Cambios' : `Crear Grupo (${selectedStudents.length} miembros)`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
